import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Divider, Spin, message } from "antd";
import { useHistory } from "react-router";
import CommonDivWidget from "components/CommonDivWidget/CommonDivWidget";
import AudienceTable from "./AudienceTable/AudienceTable";
import SearchWidget from "./SearchWidget/SearchWidget";
import CreateAudienceModal from "./CreateAudienceModal/CreateAudienceModal";
import AudienceMembersModal from "./AudienceMembersModal/AudienceMembersModal";
import { ROUTE_MESSAGE } from "navigation/CONSTANTS";
import "./AudiencePage.css";
import { fetchAudienceList, createAudience, updateAudience, deleteAudience as deleteAudienceApi, addAudienceMembers } from "services/audienceService";

const AudiencePage = () => {
  const history = useHistory();
  const userDataStore = useSelector((x) => x.userDataStore);
  const appDataStore = useSelector((x) => x.appDataStore);
  const [isBusy, setIsBusy] = useState(false);
  const [audienceList, setAudienceList] = useState([]);
  const [userData, setUserData] = useState({});
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editInitialData, setEditInitialData] = useState(null);
  const [membersModalVisible, setMembersModalVisible] = useState(false);
  const [membersAudience, setMembersAudience] = useState(null);
  const [isSavingMembers, setIsSavingMembers] = useState(false);

  useEffect(() => {
    if (!userDataStore?.token) return;

    const load = async () => {
      setIsBusy(true);
      try {
  const list = await fetchAudienceList(userDataStore?.token, appDataStore?.club?.id);
        // Log the first item to see all available fields
        if (Array.isArray(list) && list.length > 0) {
        }
        // Normalize to table fields: name, member_count, created_date
        // Also preserve type field (1=PROGRAM, 2=REGISTRATION, 3=MANUAL) for edit modal
        const normalized = (Array.isArray(list) ? list : []).map((a) => ({
          id: a.id,
          name: a.name,
          member_count: a.email_count,
          created_date: (a.created_date || '').split(' ')[0], // For display (date only)
          created_date_full: a.created_date || a.created_at || '', // For sorting (full timestamp)
          emails: a.emails,
          type: a.type ?? a.audience_type ?? a.Type ?? null, // Try multiple possible field names
          channel: a.channel || "email", // email or sms
          search_string: a.search_string ?? a.searchString ?? null,
          registration_id: a.registration_id ?? a.registrationId ?? null,
        }));

        // Sort by creation date (newest first - most recently added at the top)
        const sorted = normalized.sort((a, b) => {
          // Compare full timestamps if available, otherwise compare date strings
          const dateA = a.created_date_full || a.created_date || '';
          const dateB = b.created_date_full || b.created_date || '';
          // Sort descending (newest first)
          return dateB.localeCompare(dateA);
        });

        
        setAudienceList(sorted);
      } catch (e) {
        console.error("Error loading audience list:", e);
        message.error("Failed to load audience list");
      } finally {
        setIsBusy(false);
      }
    };
    load();
  }, [userDataStore?.token, appDataStore?.club?.id]);

  const deleteData = async (id) => {
    try {
      setIsBusy(true);
      const payload = { token: userDataStore?.token, id };
      
      await deleteAudienceApi(payload);
      setAudienceList((prev) => prev.filter((x) => x.id != id));
      message.success("Deleted successfully.");
    } catch (e) {
      message.error("Failed to delete audience");
    } finally {
      setIsBusy(false);
    }
  };

  // Open modal directly with the clicked row's data
  const openEditModal = async (info) => {
    setModalMode("edit");

    

    // Map type from backend to createFrom: 1=PROGRAM, 2=REGISTRATION, 3=MANUAL
    let createFrom = "";

    // Try to get type - check multiple possible values and handle string/number
    const typeValue = info?.type ?? info?.audience_type ?? info?.Type;
    if (typeValue !== undefined && typeValue !== null) {
      const typeNum = typeof typeValue === 'string' ? parseInt(typeValue, 10) : typeValue;
      

      if (typeNum === 1 || typeNum === '1') createFrom = "PROGRAM";
      else if (typeNum === 2 || typeNum === '2') createFrom = "REGISTRATION";
      else if (typeNum === 3 || typeNum === '3') createFrom = "MANUAL";
      else if (typeNum === 4 || typeNum === '4') createFrom = "TEAM";
    }

    // Fallback: if type is not available, infer from emails (but this is not reliable)
    // This fallback is WRONG - REGISTRATION audiences also have emails!
    // So we should default to REGISTRATION if type is missing, not MANUAL
    if (!createFrom) {
      
      // Actually, if we don't have type, we can't reliably determine it
      // But since REGISTRATION and PROGRAM audiences also have emails populated,
      // having emails doesn't mean it's MANUAL
      // Let's default to REGISTRATION as a safer guess
      createFrom = "REGISTRATION";
    }

    // Normalize emails/phone numbers for display (comma-separated string)
    const membersStr = Array.isArray(info?.emails) && info.emails.length > 0
      ? info.emails.join(", ")
      : "";
    const isSmsCh = (info.channel || "email") === "sms";
    const emailsStr = isSmsCh ? "" : membersStr;
    const phoneNumbersStr = isSmsCh ? membersStr : "";

    // For REGISTRATION and PROGRAM types, we need to map search_string (names) back to IDs
    let registrationIds = [];
    let registrationIdForProgram = null;
    let programIds = [];

    

    if (createFrom === "REGISTRATION" || createFrom === "PROGRAM") {
      // Try to fetch registrations and map - even without search_string, we can try to match by name
      // This is a fallback if search_string is not returned by the API
      if (!info?.search_string) {
        console.warn("[openEditModal] No search_string found in info object. Available keys:", Object.keys(info));
        console.warn("[openEditModal] Cannot map registration IDs without search_string. The backend API needs to return search_string in get_audiences response.");
        // TODO: We need the backend to return search_string in the get_audiences API response
      }

      // Even if search_string is missing, try to fetch registrations anyway
      // The mapping will fail, but at least registrations will be loaded in the modal
      {
        try {
          // Fetch registrations to map names back to IDs
          const { fetchRegistrations } = await import("services/audienceService");
          const clubId = appDataStore?.club?.id;
          if (clubId && userDataStore?.token) {
            const regs = await fetchRegistrations(userDataStore.token, clubId);

            const normalizedRegs = (Array.isArray(regs) ? regs : []).map((r) => ({
              id: r.id ?? r.registration_id ?? r.ID,
              name: r.name ?? r.registration_name ?? r.Name,
            })).filter(x => x.id && x.name);

            

            // Parse search_string (could be comma-separated registration names)
            // If search_string doesn't exist, we can't map - this is a backend limitation
            if (info.search_string) {
              const searchNames = (info.search_string || "").split(',').map(s => s.trim()).filter(Boolean);

              // Map registration names to IDs
              if (createFrom === "REGISTRATION") {
                registrationIds = searchNames.map(name => {
                  // Try exact match first, then case-insensitive, then trimmed
                  const reg = normalizedRegs.find(r => {
                    const rName = r.name || "";
                    return rName === name ||
                      rName.toLowerCase() === name.toLowerCase() ||
                      rName.trim() === name.trim() ||
                      rName.trim().toLowerCase() === name.trim().toLowerCase();
                  });
                  if (!reg) {
                    console.warn(`[openEditModal] No match found for registration name: "${name}"`);
                  }
                  return reg?.id;
                }).filter(Boolean);
                
              } else if (createFrom === "PROGRAM") {
                // For PROGRAM, search_string contains program names, not registration names
                // We need registration_id from the audience data
                if (info.registration_id) {
                  registrationIdForProgram = info.registration_id;
                }
                // Program names are in search_string
                programIds = searchNames;
              }
            } else {
              console.warn("[openEditModal] search_string is missing - cannot pre-select registrations. Backend API needs to return search_string field.");

              // WORKAROUND: If there's only one registration available and createFrom is REGISTRATION,
              // auto-select it as a best guess (since we can't know which one was used)
              if (createFrom === "REGISTRATION" && normalizedRegs.length === 1) {
                
                registrationIds = [normalizedRegs[0].id];
              } else if (createFrom === "REGISTRATION" && normalizedRegs.length > 1) {
                console.warn("[openEditModal] Multiple registrations available but search_string is null - cannot determine which one was used.");
                console.warn("[openEditModal] BACKEND FIX NEEDED: get_audiences API must return 'search_string' and 'type' fields");
              }
            }
          }
        } catch (error) {
          console.error("[openEditModal] Failed to load registrations for mapping:", error);
        }
      }
    }

    const normalized = {
      ...info,
      createFrom: createFrom,
      channel: info.channel || "email",
      emails: emailsStr,
      phoneNumbers: phoneNumbersStr,
      registrationIds: registrationIds.length > 0 ? registrationIds : (info.registrationIds || []),
      registrationIdForProgram: registrationIdForProgram || info.registrationIdForProgram || info.registration_id,
      programIds: programIds.length > 0 ? programIds : (info.programIds || []),
      search_string: info.search_string, // Make sure search_string is preserved
    };

    

    setEditInitialData(normalized);
    setCreateModalVisible(true);
  };

  const openMembersModal = (info) => {
    setMembersAudience(info);
    setMembersModalVisible(true);
  };

  const saveMembers = async (updatedMembers) => {
    const isSms = (membersAudience?.channel || "email") === "sms";
    const isManual = parseInt(membersAudience?.type, 10) === 3;

    try {
      setIsSavingMembers(true);

      if (isManual) {
        // Full replace
        const payload = {
          token: userDataStore?.token,
          club_id: appDataStore?.club?.id,
          audience_id: membersAudience.id,
          audience_name: membersAudience.name,
          type: 3,
          channel: isSms ? "sms" : "email",
        };
        if (isSms) {
          payload.phone_numbers = updatedMembers;
        } else {
          payload.emails = updatedMembers;
        }
        await updateAudience(payload);
      } else {
        // Add-only: send only contacts that weren't in the original list
        const initialSet = new Set(
          (membersAudience?.emails || []).map((m) =>
            typeof m === "object" && m !== null
              ? m.email || m.phone || m.value || ""
              : String(m ?? "")
          )
        );
        const newContacts = updatedMembers.filter((m) => !initialSet.has(m));
        if (newContacts.length === 0) {
          message.info("No new members to add");
          setIsSavingMembers(false);
          return;
        }
        await addAudienceMembers({
          token: userDataStore?.token,
          audience_id: membersAudience.id,
          contacts: newContacts,
          channel: isSms ? "sms" : "email",
        });
      }
      // Refresh list so member_count updates
      const list = await fetchAudienceList(userDataStore?.token, appDataStore?.club?.id);
      const normalized = (Array.isArray(list) ? list : []).map((a) => ({
        id: a.id,
        name: a.name,
        member_count: a.email_count,
        created_date: (a.created_date || "").split(" ")[0],
        created_date_full: a.created_date || a.created_at || "",
        emails: a.emails,
        type: a.type ?? a.audience_type ?? a.Type ?? null,
        channel: a.channel || "email",
        search_string: a.search_string ?? a.searchString ?? null,
        registration_id: a.registration_id ?? a.registrationId ?? null,
      }));
      const sorted = normalized.sort((a, b) => {
        const dateA = a.created_date_full || a.created_date || "";
        const dateB = b.created_date_full || b.created_date || "";
        return dateB.localeCompare(dateA);
      });
      setAudienceList(sorted);
      setMembersModalVisible(false);
      setMembersAudience(null);
      message.success("Members updated successfully!");
    } catch (e) {
      message.error(typeof e === "string" ? e : "Failed to update members");
    } finally {
      setIsSavingMembers(false);
    }
  };

  const onCreateNew = () => {
    setModalMode("create");
    setEditInitialData(null);
    setCreateModalVisible(true);
  };

  const onBackToMessages = () => {
    history.push(ROUTE_MESSAGE);
  };

  const handleCreateAudience = async (formData, mode = "create") => {
    try {
      setIsBusy(true);
      if (mode === "edit" && formData.id) {
        const type = formData.createFrom === "PROGRAM" ? 1 : formData.createFrom === "REGISTRATION" ? 2 : formData.createFrom === "TEAM" ? 4 : 3;
        // Ensure edited type matches the existing audience type per API requirements
        const originalTypeRaw = editInitialData?.type ?? editInitialData?.audience_type ?? editInitialData?.Type ?? null;
        const originalType = originalTypeRaw !== null && originalTypeRaw !== undefined
          ? (typeof originalTypeRaw === 'string' ? parseInt(originalTypeRaw, 10) : originalTypeRaw)
          : null;
        if (originalType !== null && originalType !== undefined && originalType !== type) {
          message.error('Audience type cannot be changed during edit. Please keep the original type.');
          setIsBusy(false);
          return;
        }
        const payload = {
          token: userDataStore?.token,
          club_id: appDataStore?.club?.id,
          audience_id: formData.id,
          audience_name: formData.name,
          type: type,
        };

        // Pass channel (email or sms) to backend
        if (formData.channel) payload.channel = formData.channel;

        if (type === 3) {
          if (formData.channel === "sms") {
            // SMS MANUAL: phone numbers
            let phones = (formData.phoneNumbers || "").split(',').map(x => x.trim().replace(/[^+\d]/g, '')).filter(x => x);
            phones = Array.from(new Set(phones));
            if (phones.length === 0) {
              message.error('Please provide at least one phone number');
              setIsBusy(false);
              return;
            }
            payload.phone_numbers = phones;
          } else {
            // Email MANUAL: emails
            let emails = (formData.emails || "").split(',').map(x => x.trim()).filter(x => x);
            emails = Array.from(new Set(emails.map(e => e.toLowerCase()))).map(e => e);
            if (emails.length === 0) {
              message.error('Please provide at least one email address');
              setIsBusy(false);
              return;
            }
            const invalid = emails.find(e => !/^\S+@\S+\.\S+$/.test(e));
            if (invalid) {
              message.error(`Invalid email address: ${invalid}`);
              setIsBusy(false);
              return;
            }
            payload.emails = emails;
          }
        } else if (type === 2) {
          // REGISTRATION: require search_string and registration_id(s)
          const searchString = formData.registrationNames
            ? (Array.isArray(formData.registrationNames) ? formData.registrationNames.join(',') : formData.registrationNames)
            : (formData.search_string || '');

          if (!searchString || searchString.trim() === '') {
            message.error('Please provide a search_string (registration names)');
            setIsBusy(false);
            return;
          }

          if (formData.registrationIds && formData.registrationIds.length > 0) {
            payload.registration_id = formData.registrationIds.join(',');
          } else if (formData.registration_id || formData.registrationIdForProgram) {
            payload.registration_id = formData.registration_id || formData.registrationIdForProgram;
          } else {
            // registration_id required per API for type 1/2
            message.error('registration_id is required for Registration audiences');
            setIsBusy(false);
            return;
          }

          payload.search_string = searchString;
        } else if (type === 1) {
          // PROGRAM: require search_string (program names) and registration_id
          const searchString = (formData.programIds || []).join(',');
          if (!searchString || searchString.trim() === '') {
            message.error('Please select at least one program');
            setIsBusy(false);
            return;
          }
          const regId = formData.registrationIdForProgram || formData.registration_id;
          if (!regId) {
            message.error('registration_id is required for Program audiences');
            setIsBusy(false);
            return;
          }
          payload.search_string = searchString;
          payload.registration_id = regId;
        } else if (type === 4) {
          // TEAM: require team_id
          // TODO: confirm type number with backend for TEAM audiences
          if (!formData.teamId) {
            message.error('Please select a team');
            setIsBusy(false);
            return;
          }
          payload.team_id = formData.teamId;
        }

        console.log("[EditAudience] payload:", payload);
        await updateAudience(payload);
      } else {
        // map UI form to API contract
        const type = formData.createFrom === "PROGRAM" ? 1 : formData.createFrom === "REGISTRATION" ? 2 : formData.createFrom === "TEAM" ? 4 : 3;

        // Build payload conditionally for creation
        const payload = {
          token: userDataStore?.token,
          club_id: appDataStore?.club?.id,
          audience_name: formData.name,
          type: type,
        };

        // Pass channel (email or sms) to backend
        if (formData.channel) payload.channel = formData.channel;

        if (type === 3) {
          if (formData.channel === "sms") {
            // SMS MANUAL: phone numbers
            let phones = (formData.phoneNumbers || "").split(',').map(x => x.trim().replace(/[^+\d]/g, '')).filter(x => x);
            phones = Array.from(new Set(phones));
            if (phones.length === 0) {
              message.error('Please provide at least one phone number');
              setIsBusy(false);
              return;
            }
            payload.phone_numbers = phones;
          } else {
            // MANUAL: emails array
            let emails = (formData.emails || "").split(',').map(x => x.trim()).filter(x => x);
            emails = Array.from(new Set(emails.map(e => e.toLowerCase()))).map(e => e);
            if (emails.length === 0) {
              message.error('Please provide at least one email address');
              setIsBusy(false);
              return;
            }
            const invalid = emails.find(e => !/^\S+@\S+\.\S+$/.test(e));
            if (invalid) {
              message.error(`Invalid email address: ${invalid}`);
              setIsBusy(false);
              return;
            }
            payload.emails = emails;
          }
        } else if (type === 2) {
          // REGISTRATION: require search_string (registration names) and registration_id
          const searchString = formData.registrationNames
            ? (Array.isArray(formData.registrationNames) ? formData.registrationNames.join(',') : formData.registrationNames)
            : '';

          if (!searchString || searchString.trim() === '') {
            message.error('Please select at least one registration');
            setIsBusy(false);
            return;
          }

          if (formData.registrationIds && formData.registrationIds.length > 0) {
            payload.registration_id = formData.registrationIds.join(',');
          } else if (formData.registration_id) {
            payload.registration_id = formData.registration_id;
          } else {
            message.error('registration_id is required for Registration audiences');
            setIsBusy(false);
            return;
          }

          payload.search_string = searchString;
        } else if (type === 1) {
          // PROGRAM: require search_string (program names) and registration_id
          const searchString = (formData.programIds || []).join(',');
          if (!searchString || searchString.trim() === '') {
            message.error('Please select at least one program');
            setIsBusy(false);
            return;
          }
          if (!formData.registrationIdForProgram) {
            message.error('Please select a registration');
            setIsBusy(false);
            return;
          }

          payload.search_string = searchString;
          payload.registration_id = formData.registrationIdForProgram;
        } else if (type === 4) {
          // TEAM: require team_id
          // TODO: confirm type number with backend for TEAM audiences
          // TODO: use the team email endpoint to fetch emails when available
          if (!formData.teamId) {
            message.error('Please select a team');
            setIsBusy(false);
            return;
          }
          payload.team_id = formData.teamId;
        }

        console.log("[CreateAudience] payload:", payload);
        await createAudience(payload);
      }
      // Always reload the audience list after create/edit
      try {
        const list = await fetchAudienceList(userDataStore?.token, appDataStore?.club?.id);
        const normalized = (Array.isArray(list) ? list : []).map((a) => ({
          id: a.id,
          name: a.name,
          member_count: a.email_count,
          created_date: (a.created_date || '').split(' ')[0], // For display
          created_date_full: a.created_date || a.created_at || '', // For sorting
          emails: a.emails,
          type: a.type ?? a.audience_type ?? a.Type ?? null,
          channel: a.channel || "email",
          search_string: a.search_string ?? a.searchString ?? null,
          registration_id: a.registration_id ?? a.registrationId ?? null,
        }));

        // Sort by creation date (newest first)
        const sorted = normalized.sort((a, b) => {
          const dateA = a.created_date_full || a.created_date || '';
          const dateB = b.created_date_full || b.created_date || '';
          return dateB.localeCompare(dateA);
        });

        setAudienceList(sorted);
      } catch { }
      message.success(mode === "edit" ? "Audience updated successfully!" : "Audience created successfully!");
    } catch (e) {
      console.error("[CreateAudience] Error:", e);
      // Show the actual error message from backend
      const errorMsg = typeof e === 'string' ? e : (e?.message || (mode === "edit" ? "Failed to update audience" : "Failed to create audience"));
      message.error(errorMsg);
    } finally {
      setIsBusy(false);
    }
  };

  // (no-op shadow, handled above)

  return (
    <CommonDivWidget>
      <Spin spinning={isBusy}>
        <div className="audience-page">
          <div style={{ paddingLeft: 10, paddingRight: 10 }}>
            <Divider
              style={{ borderTop: "4px solid var(--mainColor)" }}
            ></Divider>
            <SearchWidget
              onCreateNew={onCreateNew}
              onBackToMessages={onBackToMessages}
            />
            <AudienceTable
              dataList={audienceList}
              setVisible={openEditModal}
              setUserData={setUserData}
              deleteData={deleteData}
              onMembersClick={openMembersModal}
            />
          </div>
        </div>
      </Spin>

      <CreateAudienceModal
        visible={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        onSubmit={handleCreateAudience}
        mode={modalMode}
        initialData={editInitialData}
        smsEnabled={!!userDataStore.sms_enabled}
        token={userDataStore?.token}
        clubId={appDataStore?.club?.id}
        availableTeams={userDataStore?.teams}
      />

      <AudienceMembersModal
        visible={membersModalVisible}
        audience={membersAudience}
        onCancel={() => { setMembersModalVisible(false); setMembersAudience(null); }}
        onSave={saveMembers}
        isSaving={isSavingMembers}
      />
    </CommonDivWidget>
  );
};

export default AudiencePage;